import type { DropdownData, FilteredValue, Value } from '../../interfaces/input.interface';

export type ReactiveSelectionPrimitiveId = string | number;

export interface ReactiveSelectionResolverConfiguration {
    readonly idKey?: string;
    readonly idKeys?: readonly string[];
    readonly separators?: RegExp;
}

interface ReactiveContextValueReader {
    readonly getValue: (fieldName: string) => unknown;
}

interface ReactiveModelGetterContext {
    readonly model: {
        readonly get: (fieldName: string) => { readonly value: unknown } | null;
    };
}

const DEFAULT_ID_KEY = 'id';
const DEFAULT_ID_KEYS = [DEFAULT_ID_KEY] as const;
const DEFAULT_VALUE_SEPARATORS = /[;,]/u;

/**
 * Resolves primitive ids and selected dropdown values from saved form-control values.
 *
 * Form controls in legacy forms may store primitives, comma/semicolon strings, raw entities or arrays.
 * Keeping this logic centralized avoids each host component implementing its own id parser.
 */
export class ReactiveSelectionResolver {
    public static extractId(
        value: unknown,
        configuration: ReactiveSelectionResolverConfiguration = {},
    ): ReactiveSelectionPrimitiveId | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        if (typeof value === 'string' || typeof value === 'number') {
            return value;
        }

        if (typeof value !== 'object') {
            return null;
        }

        for (const idKey of this.getIdKeys(configuration)) {
            const id = (value as Record<string, unknown>)[idKey];

            if (typeof id === 'string' || typeof id === 'number') {
                return id;
            }
        }

        return null;
    }

    public static extractIdTokens(
        value: unknown,
        configuration: ReactiveSelectionResolverConfiguration = {},
    ): Set<string> {
        return new Set(this.extractIdList(value, configuration));
    }

    public static extractIdList(
        value: unknown,
        configuration: ReactiveSelectionResolverConfiguration = {},
    ): string[] {
        const tokens: string[] = [];

        this.collectIdTokens(value, tokens, configuration);

        return tokens;
    }

    public static normalizeId(
        value: unknown,
        configuration: ReactiveSelectionResolverConfiguration = {},
    ): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        if (typeof value === 'object') {
            const directId = this.extractId(value, configuration);

            if (directId !== null) {
                return this.normalizeId(directId, configuration);
            }

            if ('value' in value) {
                return this.normalizeId((value as Record<string, unknown>)['value'], configuration);
            }

            return null;
        }

        const normalized = String(value).trim();

        return normalized === '' || normalized === 'null' || normalized === 'undefined' ? null : normalized;
    }


    public static toPositiveIntegerId(
        value: unknown,
        configuration: ReactiveSelectionResolverConfiguration = {},
    ): number | null {
        const normalizedId = this.normalizeId(value, configuration);

        if (normalizedId === null) {
            return null;
        }

        const numericId = Number(normalizedId);

        return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
    }


    public static readContextValue(context: unknown, fieldName: string): unknown {
        if (this.hasContextValueReader(context)) {
            return context.getValue(fieldName);
        }

        if (this.hasModelGetterContext(context)) {
            return context.model.get(fieldName)?.value;
        }

        return undefined;
    }

    public static extractEntities<T>(
        value: unknown,
        configuration: ReactiveSelectionResolverConfiguration = {},
    ): T[] {
        const values = Array.isArray(value) ? value : [value];

        return values.filter((item): item is T => (
            typeof item === 'object' && item !== null && this.extractId(item, configuration) !== null
        ));
    }

    public static areSelectionsEquivalent(
        currentValue: unknown,
        nextValue: unknown,
        configuration: ReactiveSelectionResolverConfiguration = {},
    ): boolean {
        const currentIds = this.extractIdList(currentValue, configuration);
        const nextIds = this.extractIdList(nextValue, configuration);

        return currentIds.length === nextIds.length && currentIds.every((id, index) => id === nextIds[index]);
    }

    public static resolveSelectedValues<T>(
        dropdown: DropdownData<T> | null | undefined,
        rawValue: unknown,
        configuration: ReactiveSelectionResolverConfiguration = {},
    ): T[] {
        const selectedTokens = this.extractIdTokens(rawValue, configuration);

        return this.resolveValuesByTokens(dropdown, selectedTokens, configuration);
    }

    public static resolveSelectedEntities<T>(
        dropdown: DropdownData<T> | null | undefined,
        rawValue: unknown,
        configuration: ReactiveSelectionResolverConfiguration = {},
    ): T[] {
        const rawEntities = this.extractEntities<T>(rawValue, configuration);

        return rawEntities.length > 0 ? rawEntities : this.resolveSelectedValues(dropdown, rawValue, configuration);
    }

    public static resolveValueById<T>(
        dropdown: DropdownData<T> | null | undefined,
        id: unknown,
        configuration: ReactiveSelectionResolverConfiguration = {},
    ): T | null {
        const selectedTokens = this.extractIdTokens(id, configuration);

        return this.resolveValuesByTokens(dropdown, selectedTokens, configuration)[0] ?? null;
    }

    public static resolveValuesByIds<T>(
        dropdown: DropdownData<T> | null | undefined,
        ids: Iterable<unknown>,
        configuration: ReactiveSelectionResolverConfiguration = {},
    ): T[] {
        const selectedTokens = new Set<string>();

        for (const id of ids) {
            this.collectIdTokens(id, selectedTokens, configuration);
        }

        return this.resolveValuesByTokens(dropdown, selectedTokens, configuration);
    }

    public static matchesValue(
        candidate: unknown,
        selectedTokens: ReadonlySet<string>,
        configuration: ReactiveSelectionResolverConfiguration = {},
    ): boolean {
        if (candidate === null || candidate === undefined) {
            return false;
        }

        const candidateTokens = this.extractIdList(candidate, configuration);

        if (candidateTokens.length > 0) {
            return candidateTokens.some((token) => selectedTokens.has(token));
        }

        return selectedTokens.has(String(candidate));
    }

    private static resolveValuesByTokens<T>(
        dropdown: DropdownData<T> | null | undefined,
        selectedTokens: ReadonlySet<string>,
        configuration: ReactiveSelectionResolverConfiguration,
    ): T[] {
        if (!dropdown || selectedTokens.size === 0) {
            return [];
        }

        const selected: T[] = [];
        const seenIds = new Set<string>();

        for (const option of this.getDropdownOptions(dropdown)) {
            if (!this.optionMatchesTokens(option, selectedTokens, configuration)) {
                continue;
            }

            const selectedValue = this.resolveSelectedOptionValue(option);
            const selectedId = this.extractId(selectedValue, configuration);
            const selectedKey = selectedId === null ? String(selectedValue) : String(selectedId);

            if (seenIds.has(selectedKey)) {
                continue;
            }

            selected.push(selectedValue);
            seenIds.add(selectedKey);
        }

        return selected;
    }

    private static getDropdownOptions<T>(dropdown: DropdownData<T>): Array<Value<T> | FilteredValue<T>> {
        return [
            ...(dropdown.filtered ?? []),
            ...(dropdown.values ?? []),
        ];
    }

    private static optionMatchesTokens<T>(
        option: Value<T> | FilteredValue<T>,
        selectedTokens: ReadonlySet<string>,
        configuration: ReactiveSelectionResolverConfiguration,
    ): boolean {
        const candidates = [
            option.value,
            option.raw,
            option.label,
            ...(option.valueAliases ?? []),
        ];

        return candidates.some((candidate) => this.matchesValue(candidate, selectedTokens, configuration));
    }

    private static resolveSelectedOptionValue<T>(option: Value<T> | FilteredValue<T>): T {
        return (option.raw ?? option.value ?? null) as T;
    }


    private static hasContextValueReader(context: unknown): context is ReactiveContextValueReader {
        return this.isRecord(context) && typeof context['getValue'] === 'function';
    }

    private static hasModelGetterContext(context: unknown): context is ReactiveModelGetterContext {
        return this.isRecord(context) && this.isRecord(context['model']) && typeof context['model']['get'] === 'function';
    }

    private static isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    private static collectIdTokens(
        value: unknown,
        tokens: string[] | Set<string>,
        configuration: ReactiveSelectionResolverConfiguration,
    ): void {
        if (value === null || value === undefined || value === '') {
            return;
        }

        if (Array.isArray(value)) {
            for (const item of value) {
                this.collectIdTokens(item, tokens, configuration);
            }

            return;
        }

        if (typeof value === 'number') {
            this.addToken(tokens, String(value));

            return;
        }

        if (typeof value === 'string') {
            this.collectDelimitedStringTokens(value, tokens, configuration);

            return;
        }

        for (const idKey of this.getIdKeys(configuration)) {
            const id = (value as Record<string, unknown>)[idKey];

            if (typeof id === 'string' || typeof id === 'number') {
                this.addToken(tokens, String(id));
            }
        }
    }

    private static getIdKeys(configuration: ReactiveSelectionResolverConfiguration): readonly string[] {
        const configuredKeys = [
            configuration.idKey,
            ...(configuration.idKeys ?? []),
        ].filter((key): key is string => typeof key === 'string' && key.trim() !== '');

        if (configuredKeys.length === 0) {
            return DEFAULT_ID_KEYS;
        }

        return configuredKeys.filter((key, index, keys) => keys.indexOf(key) === index);
    }

    private static collectDelimitedStringTokens(
        value: string,
        tokens: string[] | Set<string>,
        configuration: ReactiveSelectionResolverConfiguration,
    ): void {
        const rawValue = value.trim();

        if (rawValue === '') {
            return;
        }

        for (const part of rawValue.split(configuration.separators ?? DEFAULT_VALUE_SEPARATORS)) {
            const token = part.trim();

            if (token !== '') {
                this.addToken(tokens, token);
            }
        }
    }

    private static addToken(tokens: string[] | Set<string>, token: string): void {
        if (tokens instanceof Set) {
            tokens.add(token);
            return;
        }

        if (!tokens.includes(token)) {
            tokens.push(token);
        }
    }
}
